import { ORPCError } from '@orpc/client'
import { oc } from '@orpc/contract'
import { z } from 'zod'

export const upstreamSchema = z.enum(['core', 'bridge', 'credora', 'eas', 'merkl', 'actions'])

export const sharedErrors = {
  NOT_FOUND: { status: 404, message: 'Resource not found' },
  UPSTREAM_UNAVAILABLE: {
    status: 502,
    message: 'Upstream unavailable',
    data: z.object({ upstream: upstreamSchema })
  },
  UPSTREAM_SCHEMA: {
    status: 502,
    message: 'Upstream response failed validation',
    data: z.object({ upstream: upstreamSchema })
  }
} as const

export type SharedErrorCode = keyof typeof sharedErrors
export type Upstream = z.infer<typeof upstreamSchema>

/**
 * Whether an error is one of the shared errors, by code and status. Unlike `defined`, this
 * also holds for an error built with `new ORPCError` before oRPC validated it.
 */
export function isSharedError(error: ORPCError<string, unknown>): boolean {
  return (
    Object.hasOwn(sharedErrors, error.code) &&
    sharedErrors[error.code as SharedErrorCode].status === error.status
  )
}

/**
 * The typed upstream error for code outside a handler (the upstream clients), where the
 * `errors` helper is not available. Details go in `cause`: it is logged, never sent.
 */
export function upstreamError(
  code: 'UPSTREAM_UNAVAILABLE' | 'UPSTREAM_SCHEMA',
  upstream: Upstream,
  cause?: unknown
): ORPCError<typeof code, { upstream: Upstream }> {
  return new ORPCError(code, {
    status: sharedErrors[code].status,
    message: sharedErrors[code].message,
    data: { upstream },
    cause
  })
}

/**
 * The typed `NOT_FOUND` for code outside a handler, where the `errors` helper is not available.
 * Companion to {@link upstreamError}: an upstream 404 is an expected answer, not a failure.
 */
export function notFoundError(cause?: unknown): ORPCError<'NOT_FOUND', undefined> {
  return new ORPCError('NOT_FOUND', {
    status: sharedErrors.NOT_FOUND.status,
    message: sharedErrors.NOT_FOUND.message,
    cause
  })
}

/**
 * The shared error code of an oRPC error, for UI branching (never branch on messages).
 * Works for client queries, where the error crosses HTTP, and for server components
 * that catch the in-process call themselves. Production strips server-component errors
 * to a digest before they reach an error boundary, so a server component reads the code
 * through `callSection` (`NOT_FOUND` → `notFound()`, `UPSTREAM_*` → its degraded state).
 */
export function errorCodeOf(error: unknown): SharedErrorCode | 'UNKNOWN' {
  if (error instanceof ORPCError && isSharedError(error)) return error.code as SharedErrorCode
  return 'UNKNOWN'
}

export type ProcedureMeta = { pendingConsumer?: string }

/** Base every contract builds on: all procedures share the typed error set. */
export const base = oc.$meta<ProcedureMeta>({}).errors(sharedErrors)
